import {
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import { useFrontendAuth } from './useAuth';

export function useProtectedQuery<
    TQueryFnData = unknown,
    TError = unknown,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[]
>(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> {
    const { isAuthenticated, isAuthReady, user } = useFrontendAuth();

    // CRITICAL: Inject userId into queryKey for private data.
    // This guarantees that different logged-in accounts get completely separate cache entries.
    // Prevents ANY bleed of myProducts, myOrders, userProfile, favourites, notifications, myChildren etc.
    // Even if removeQueries/clears are missed, keys differ per user.
    const baseKey = options.queryKey as readonly unknown[];
    const userId = user?.id;
    const finalQueryKey = userId
        ? [...baseKey, { userId }]
        : baseKey;

    // Defaults first, then caller options — so NotificationBell can set refetchInterval / window focus.
    // Previously hard-coded overrides after ...options killed refetchOnWindowFocus for the bell (#124).
    return useQuery({
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        networkMode: 'offlineFirst',
        staleTime: options.staleTime ?? 5 * 60 * 1000,
        ...options,
        queryKey: finalQueryKey as unknown as TQueryKey,
        enabled: isAuthReady && isAuthenticated && (options.enabled ?? true),
    });
}